// src/hooks/useFloorSelection.js (HTMLのモード遷移ロジックに準拠)
import React, { useState, useCallback } from 'react';

export const useFloorSelection = (handicap) => {
  const [selectedCurrentFloor, setSelectedCurrentFloor] = useState('-');
  const [selectedDestinationFloor, setSelectedDestinationFloor] = useState('-');
  const [currentSelectionMode, setCurrentSelectionMode] = useState('current');

  const setFloorSelection = useCallback((currentFloor, destinationFloor, mode = 'call_ready') => {
    setSelectedCurrentFloor(currentFloor);
    setSelectedDestinationFloor(destinationFloor);
    setCurrentSelectionMode(mode);
  }, []);

  // 階選択処理（HTMLと完全に同じロジック）
  const handleFloorClick = useCallback((floor) => {
    const trimmedFloor = floor.trim();

    // HTMLのhandleFloorClick関数と同じ処理順序

    // 1. 既に選択済みの階を再選択した場合の処理（HTMLと同じ条件）
    if (trimmedFloor === selectedCurrentFloor && currentSelectionMode !== 'current') {
      // 今いる階を再選択した場合、現在選択モードに戻して今いる階リセット
      setSelectedCurrentFloor('-');
      setCurrentSelectionMode('current');
      return;
    }

    if (trimmedFloor === selectedDestinationFloor && currentSelectionMode !== 'destination') {
      // 行きたい階を再選択した場合、行先選択モードに戻して行きたい階リセット
      setSelectedDestinationFloor('-');
      // 両方未選択になった場合は必ず現在選択モードに
      if (selectedCurrentFloor === '-') {
        setCurrentSelectionMode('current');
      } else {
        setCurrentSelectionMode('destination');
      }
      return;
    }

    // 2. 通常の階選択処理（HTMLと同じ分岐）
    if (currentSelectionMode === 'current') {
      // 今いる階選択
      setSelectedCurrentFloor(trimmedFloor);

      if (selectedDestinationFloor !== '-') {
        // 行先も選択済みなら呼び出しモードへ
        setCurrentSelectionMode('call_ready');
      } else {
        // 行先未選択なら行先選択モードへ
        setCurrentSelectionMode('destination');
      }

    } else if (currentSelectionMode === 'destination') {
      // 行きたい階選択
      setSelectedDestinationFloor(trimmedFloor);

      if (selectedCurrentFloor !== '-') {
        // 現在も選択済みなら呼び出しモードへ
        setCurrentSelectionMode('call_ready');
      } else {
        // 現在未選択なら現在選択モードへ
        setCurrentSelectionMode('current');
      }

    } else if (currentSelectionMode === 'call_ready') {
      // CALL_READYモードでの階選択処理
      if (trimmedFloor !== selectedCurrentFloor && trimmedFloor !== selectedDestinationFloor) {
        // 新しい階を選択した場合
        // HTMLでは「より最近に選択された階を更新する（とりあえずDestinationを更新）」
        setSelectedDestinationFloor(trimmedFloor);
        // モードはCALL_READYのまま
      }
      // 既に選択済みの階の場合は上の再選択処理で処理済み
    }
  }, [currentSelectionMode, selectedCurrentFloor, selectedDestinationFloor]);

  // 階の入れ替え処理（HTMLと同じ）
  const handleSwapFloors = useCallback(() => {
    if (selectedCurrentFloor !== '-' && selectedDestinationFloor !== '-') {
      const temp = selectedCurrentFloor;
      setSelectedCurrentFloor(selectedDestinationFloor);
      setSelectedDestinationFloor(temp);
      // CALL_READYモードを維持
      setCurrentSelectionMode('call_ready');
    }
  }, [selectedCurrentFloor, selectedDestinationFloor]);

  // 現在階表示エリアのクリック（HTMLと同じ）
  const handleCurrentFloorClick = useCallback(() => {
    if (selectedCurrentFloor !== '-') {
      // 選択済みの今いる階をクリックした場合、現在選択モードに戻る
      setCurrentSelectionMode('current');
    }
  }, [selectedCurrentFloor]);

  // 行先階表示エリアのクリック（HTMLと同じ）
  const handleDestinationFloorClick = useCallback(() => {
    if (selectedDestinationFloor !== '-') {
      // 選択済みの行きたい階をクリックした場合、行先選択モードに戻る
      setCurrentSelectionMode('destination');
    }
  }, [selectedDestinationFloor]);

  // プレースホルダーキャンセル処理（HTMLのupdatePlaceholderDisplayと同じ）
  const handlePlaceholderCancel = useCallback((labelText, floor) => {
    // HTMLの固定表示キャンセル処理と同じロジック
    if (labelText === '現在') {
      setSelectedCurrentFloor('-');
      if (selectedDestinationFloor === '-') {
        setCurrentSelectionMode('current');
      } else {
        setCurrentSelectionMode('current');
      }
    } else if (labelText === '行先') {
      setSelectedDestinationFloor('-');
      if (selectedCurrentFloor === '-') {
        setCurrentSelectionMode('current');
      } else {
        setCurrentSelectionMode('destination');
      }
    }
  }, [selectedCurrentFloor, selectedDestinationFloor]);

  // 選択状態のリセット（HTMLのresetSelection関数と同じ）
  const resetSelection = useCallback(() => {
    setSelectedCurrentFloor('-');
    setSelectedDestinationFloor('-');
    setCurrentSelectionMode('current');
  }, []);

  // エレベータ選択時のリセット
  const resetForNewElevator = useCallback(() => {
    setSelectedCurrentFloor('-');
    setSelectedDestinationFloor('-');
    setCurrentSelectionMode('current');
  }, []);

  // インストラクションテキストの取得（HTMLのupdateUIForCurrentModeと同じ）
  const getInstructionText = useCallback((currentRegNumber) => {
    // HTMLの条件分岐と同じ順序
    if (currentRegNumber === null) {
      // エレベータ未選択時は常に非アクティブ
      return (
        <>
          <span className="ux-text-darkgray">エレベータ</span><span className="ux-text-gray ml-1">を選択</span>
        </>
      );
    }

    switch (currentSelectionMode) {
      case 'current':
        return (
          <>
            <span className="ux-text-darkgray">今いる階</span><span className="ux-text-gray ml-1">を選択</span>
          </>
        );
      case 'destination':
        return (
          <>
            <span className="ux-text-darkgray">行きたい階</span><span className="ux-text-gray ml-1">を選択</span>
          </>
        );
      case 'call_ready':
        return (
          <>
            <span className="ux-text-darkgray">呼ぶ</span><span className="ux-text-gray ml-1">を押す</span>
          </>
        );
      default:
        return <><span className="ux-text-darkgray">エレベータ</span><span className="ux-text-gray ml-1">を選択</span></>;
    }
  }, [currentSelectionMode]);

  // 両方の階が選択されているかチェック（HTMLのbothFloorsSelectedと同じ）
  const areBothFloorsSelected = useCallback(() => {
    return selectedCurrentFloor !== '-' && selectedDestinationFloor !== '-';
  }, [selectedCurrentFloor, selectedDestinationFloor]);

  // CALL_READYモードの表示判定（HTMLと同じ条件）
  const shouldShowCallReady = useCallback(() => {
    return currentSelectionMode === 'call_ready' && areBothFloorsSelected();
  }, [currentSelectionMode, areBothFloorsSelected]);

  // 呼ぶボタンの有効性判定（HTMLのupdateUIForCurrentModeと同じ）
  const isCallButtonEnabled = useCallback((currentRegNumber) => {
    if (!currentRegNumber) return false;

    const bothFloorsSelected = areBothFloorsSelected();

    switch (currentSelectionMode) {
      case 'current':
      case 'destination':
        // 現在選択・行先選択モード：両方選択済みなら有効
        return bothFloorsSelected;
      case 'call_ready':
        // 呼び出しモード：常に有効
        return true;
      default:
        return false;
    }
  }, [currentSelectionMode, areBothFloorsSelected]);

  return {
    // State values
    selectedCurrentFloor,
    selectedDestinationFloor,
    currentSelectionMode,

    // State setters
    setSelectedCurrentFloor,
    setSelectedDestinationFloor,
    setCurrentSelectionMode,
    setFloorSelection,

    // Event handlers
    handleFloorClick,
    handleSwapFloors,
    handleCurrentFloorClick,
    handleDestinationFloorClick,
    handlePlaceholderCancel,

    // Utility functions
    resetSelection,
    resetForNewElevator,
    getInstructionText,
    areBothFloorsSelected,
    shouldShowCallReady,
    isCallButtonEnabled, // 追加
  };
};

export default useFloorSelection;